/**
 * ColourMatchProShell — the single, unified professional shell.
 *
 * Product direction (LOCKED · see /app/memory/COLOURMATCH_PRO_LOCKED.md):
 *   ColourMatch Pro is the professional rendering, presentation, proposal
 *   and quotation workspace for painting and decorating businesses. The
 *   public business listing is a secondary membership benefit and not the
 *   purpose of the product.
 *
 * This shell replaces the previous PainterLayout + ProLayout duo. All old
 * routes redirect into `/pro/*` (see App.js). The two old shells remain on
 * disk but are disconnected from the route table — kept for rollback
 * safety until the new shell has passed browser E2E + regression.
 *
 * Access rules
 * ------------
 * • Any signed-in painter (`user.is_painter`) enters.
 * • Owners (`user.is_owner`) bypass for founder review.
 * • Anonymous → /login?next=<current>.
 * • Signed-in customers see a soft, product-first message (no "approval"
 *   language) inviting them to register their business — never a hard gate.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";
import {
  LayoutDashboard,
  Palette,
  FolderKanban,
  FileText,
  Users,
  CreditCard,
  UserCircle,
  ChevronRight,
  LogOut,
  Sparkles,
} from "lucide-react";

// COLOURMATCH PLATFORM DIRECTION (LOCKED 2026-02):
//   ColourMatch Pro is a professional colour visualisation platform first.
//   The Colour Workspace (rendering engine) is the primary action.
//   Everything else supports, presents, and enhances the render experience.
//
// Locked sidebar order:
//   1. Dashboard
//   2. Colour Workspace  ← primary CTA (visualisation entry point)
//   3. Client Projects
//   4. Visualisation Proposals  (evolved from "Presentations")
//   5. Leads      (was "Customers" / "Client Leads" — renamed 2026-02 as part of Gate 2 · S4
//                  when homeowner-shared visualisations were merged into this page)
//   6. Membership
//   7. Profile & Settings
//
// Business tools (Quotes, Jobs) remain reachable but visually subordinate —
// they support the visualisation, not the other way round.
const PRIMARY_NAV = [
  { to: "/pro",               label: "Dashboard",                icon: LayoutDashboard, end: true,  testid: "pro-nav-dashboard" },
  { to: "/pro/renders",       label: "Colour Workspace",         icon: Palette,         end: false, testid: "pro-nav-colour-workspace", primary: true },
  { to: "/pro/projects",      label: "Client Projects",          icon: FolderKanban,    end: false, testid: "pro-nav-client-projects" },
  { to: "/pro/presentations", label: "Visualisation Proposals",  icon: Sparkles,        end: false, testid: "pro-nav-proposals" },
  { to: "/pro/leads",         label: "Leads",                    icon: Users,           end: false, testid: "pro-nav-leads" },
  { to: "/pro/billing",       label: "Membership",               icon: CreditCard,      end: false, testid: "pro-nav-membership" },
  { to: "/pro/business",      label: "Profile & Settings",       icon: UserCircle,      end: false, testid: "pro-nav-profile-settings" },
];

const BUSINESS_NAV = [
  { to: "/pro/quotes",   label: "Quotes",           icon: FileText, end: false, testid: "pro-nav-quotes" },
];

export default function ColourMatchProShell() {
  usePageMeta({
    title: "ColourMatch Pro — Professional workspace for painting and decorating businesses",
    description: "The professional rendering, presentation, proposal and quotation workspace for painting and decorating businesses.",
    path: "/pro",
  });

  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [me, setMe] = useState(null);
  const [jobsAccess, setJobsAccess] = useState(false);
  const [openLeadCount, setOpenLeadCount] = useState(0);

  const refreshMe = useCallback(async () => {
    try {
      const r = await api.get("/painter/me");
      setMe(r.data);
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    if (user === false) {
      nav(`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`, { replace: true });
      return;
    }
    if (user && (user.is_painter || user.is_owner)) {
      // Painter (or owner-preview) — load me + counts + jobs access in parallel.
      Promise.all([
        api.get("/painter/me").then((r) => r.data).catch(() => null),
        api.get("/pro/jobs/access").then((r) => Boolean(r.data?.allowed)).catch(() => false),
        api.get("/painter/leads").then((r) => (r.data?.items || []).filter((l) => l.painter_lead_status === "new").length).catch(() => 0),
      ]).then(([meData, jobs, leads]) => {
        setMe(meData);
        setJobsAccess(jobs);
        setOpenLeadCount(leads);
      });
    }
  }, [user, nav, loc.pathname, loc.search]);

  // Gate 2 · Step S5 (2026-02-24, founder-locked): replay a stashed
  // homeowner→painter invite exactly once, and ONLY after the painter is
  // both approved AND active. The token is stashed to sessionStorage by
  // /shared-invite/:token (S1) when the visitor wasn't yet a Pro painter.
  // Safety rules:
  //   • Never grant Pro access here — we only navigate to the existing
  //     /shared-invite/:token page, which re-verifies status on the server.
  //   • Only redirect when painter_status === "approved_active" AND the
  //     invited email matches the signed-in user (server-authoritative).
  //   • Clear the sessionStorage token before navigating so the shell
  //     never loops on subsequent /pro/* mounts.
  //   • If the invite is revoked / expired / 404, drop the token so we
  //     don't re-check it on every dashboard load.
  useEffect(() => {
    if (!user || !user.is_painter) return;
    let stashedToken = null;
    try { stashedToken = window.sessionStorage.getItem("cm.invite_token"); } catch { /* noop */ }
    if (!stashedToken) return;
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get(`/shared-invite/${stashedToken}`);
        if (dead) return;
        const emailMatch = (data?.painter_email || "").toLowerCase() === (user.email || "").toLowerCase();
        if (data?.painter_status === "approved_active" && emailMatch) {
          try { window.sessionStorage.removeItem("cm.invite_token"); } catch { /* noop */ }
          nav(`/shared-invite/${stashedToken}`, { replace: true });
        }
        // Any other status (registered_not_approved, approved_inactive) —
        // leave the token stashed so a later mount, after approval + active
        // membership, can replay it.
      } catch (e) {
        const s = e?.response?.status;
        if (s === 404 || s === 410) {
          try { window.sessionStorage.removeItem("cm.invite_token"); } catch { /* noop */ }
        }
        // Other errors: leave the token, retry next mount.
      }
    })();
    return () => { dead = true; };
  }, [user, nav]);

  if (user === undefined || user === null) return null;

  // Non-painter, non-owner signed-in user → soft, product-first message.
  // Never say "approval required" — see COLOURMATCH_PRO_LOCKED.md.
  if (user && !user.is_painter && !user.is_owner) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-6" data-testid="pro-access-denied">
        <div className="max-w-lg w-full border border-brand-linen bg-white p-8 lg:p-10 text-center rounded-2xl">
          <div className="overline mb-3 text-brand-teal">ColourMatch Pro</div>
          <h1 className="font-serif text-2xl lg:text-3xl text-brand-ink mb-3">
            ColourMatch Pro is the professional workspace for painting and decorating businesses.
          </h1>
          <p className="text-sm text-brand-muted mb-7 max-w-md mx-auto leading-relaxed">
            Manage client projects, create professional renders, produce colour schedules, build presentations and win more work.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/pro/register"
              data-testid="pro-register-business-link"
              className="inline-flex items-center px-6 h-11 bg-brand-navy text-white text-sm font-semibold hover:bg-brand-teal transition-colors rounded-xl"
            >
              Register your business
            </Link>
            <Link to="/" className="text-xs text-brand-muted underline">
              Back to ColourMatch
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const businessName = me?.business_name || (user?.is_owner && !user?.is_painter ? "Owner preview" : "Your business");

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="pro-workspace">
      {/* ---- Header ------------------------------------------------------- */}
      <header className="border-b border-brand-linen bg-white">
        <div className="px-5 lg:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/pro" className="min-w-0" data-testid="pro-brand-link">
            <div className="font-serif text-2xl text-brand-ink leading-none">
              ColourMatch<span className="text-brand-teal"> Pro</span>
            </div>
            <div className="text-[11px] text-brand-muted mt-1 hidden sm:block">
              Professional workspace for painting and decorating businesses
            </div>
          </Link>
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="hidden md:block text-sm text-brand-ink font-medium truncate max-w-[220px]"
              data-testid="pro-business-name"
              title={businessName}
            >
              {businessName}
            </div>
            <button
              type="button"
              onClick={async () => { try { await logout?.(); } catch { /* ignore */ } nav("/", { replace: true }); }}
              className="inline-flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-ink transition-colors border border-brand-linen h-9 px-3 rounded-full"
              data-testid="pro-sign-out"
            >
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </header>

      {/* ---- Body --------------------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]">
        {/* ---- Sidebar --------------------------------------------------- */}
        <aside className="border-r border-brand-linen bg-white p-4 lg:min-h-[calc(100vh-73px)]" data-testid="pro-sidebar">
          <nav className="space-y-6">
            <NavGroup label="ColourMatch Pro">
              {PRIMARY_NAV.map((item) => (
                <NavItem
                  key={item.to}
                  {...item}
                  badge={item.to === "/pro/leads" && openLeadCount > 0 ? openLeadCount : null}
                />
              ))}
            </NavGroup>

            <NavGroup label="Business tools" muted>
              {BUSINESS_NAV.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
              {/* Jobs entry removed 2026-02-19 per LOCKED sidebar spec —
                  Business tools group is Quotes-only until the Jobs product
                  ships and earns a dedicated seat. */}
            </NavGroup>
          </nav>
        </aside>

        {/* ---- Main ------------------------------------------------------ */}
        <main className="p-5 lg:p-10 max-w-6xl w-full">
          <Outlet context={{ me, refreshMe, jobsAccess, openLeadCount }} />
        </main>
      </div>
    </div>
  );
}

function NavGroup({ label, children, muted = false }) {
  return (
    <div>
      <div className={`overline mb-2 px-3 ${muted ? "text-brand-muted/70" : "text-brand-muted"}`}>{label}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, end, testid, badge = null, primary = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) => {
        if (primary) {
          // "Colour Workspace" — the primary action of the whole product.
          // Rendered as a filled navy pill even when not on the current route,
          // so the painter always sees where to start a new visualisation.
          return `group flex items-center gap-3 px-3 py-2.5 text-sm font-semibold transition-colors rounded-lg ${
            isActive
              ? "bg-brand-navy text-white shadow-navy"
              : "bg-brand-navy/95 text-white hover:bg-brand-navy shadow-navy/50"
          }`;
        }
        return `group flex items-center gap-3 px-3 py-2 text-sm transition-colors rounded-lg ${
          isActive
            ? "bg-brand-teal/10 text-brand-teal font-medium"
            : "text-brand-ink hover:bg-brand-linen/40"
        }`;
      }}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {badge != null && (
        <span className="text-[10px] font-bold bg-brand-navy text-white px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-tight">
          {badge}
        </span>
      )}
      {!primary && (
        <ChevronRight className="w-3.5 h-3.5 text-brand-muted opacity-0 group-hover:opacity-40 transition-opacity" />
      )}
    </NavLink>
  );
}
